import type { Row } from '@getaccept/editor-lib';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-commenting/types/update-comment-dto';
import type { UpdateCommentsDto } from '@getaccept/lib-shared/src/contextual-commenting/types/update-comments-dto';
import type { SplitTextCreateRow } from '../types';

export const SPLIT_ROW_SECOND_NODE_ID_PLACEHOLDER = 'split-row-second-node-id-placeholder';

export class UpdateCommentsDtoHelper {
  static getCommentsInBlock(comments: Comment[], blockIds: string[]): UpdateCommentDto[] {
    return comments
      .filter(
        comment =>
          comment.editorSelection &&
          !comment.editorSelection.detachedFromVersion &&
          blockIds.includes(comment.editorSelection.editorBlockId)
      )
      .map(comment => {
        const { ...editorSelection } = comment.editorSelection;
        delete (editorSelection as any).__typename;
        delete (editorSelection as any).commentId;
        delete (editorSelection as any).detachedFromVersion;

        if (!comment.editorSelection?.textSelection) {
          return { commentId: comment.id, editorSelection };
        }

        const { ...textSelection } = comment.editorSelection.textSelection;
        delete (textSelection as any).__typename;

        return {
          commentId: comment.id,
          editorSelection: {
            ...editorSelection,
            textSelection,
          },
        };
      });
  }

  static getDeleteRowDto(
    allComments: Comment[],
    blockId: string,
    targetRowId: string
  ): UpdateCommentsDto {
    const commentsInBlock = UpdateCommentsDtoHelper.getCommentsInBlock(allComments, [blockId]);
    const commentUpdates = commentsInBlock.map(({ commentId, editorSelection }) => {
      if (editorSelection?.rowId === targetRowId) {
        return {
          commentId,
          editorSelection: {
            ...editorSelection,
            rowId: undefined,
            cellId: undefined,
            nodeId: undefined,
            textSelection: undefined,
            detachedFromContent: true,
          },
        };
      }

      return { commentId, editorSelection };
    });

    return { comments: commentUpdates };
  }

  static getUpdateColumnsCountDto(
    allComments: Comment[],
    blockId: string,
    row: Row,
    newColumnsCount: number
  ): UpdateCommentsDto {
    const commentsInBlock = UpdateCommentsDtoHelper.getCommentsInBlock(allComments, [blockId]);

    if (row.cells.length <= newColumnsCount) {
      return { comments: commentsInBlock };
    }

    const commentUpdates = commentsInBlock.map(({ commentId, editorSelection }) => {
      if (editorSelection?.rowId !== row.id) {
        return {
          commentId,
          editorSelection,
        };
      }

      const commentColumnIndex = row.cells.findIndex(cell => cell.id === editorSelection.cellId);
      const currentColumnCount = commentColumnIndex + 1;

      if (currentColumnCount > newColumnsCount) {
        return {
          commentId,
          editorSelection: {
            ...editorSelection,
            cellId: undefined,
            nodeId: undefined,
            textSelection: undefined,
            detachedFromContent: true,
          },
        };
      }

      return {
        commentId,
        editorSelection,
      };
    });

    return { comments: commentUpdates };
  }

  static getDeleteSectionDto(
    allComments: Comment[],
    blockId: string,
    targetSectionId: string
  ): UpdateCommentsDto {
    const commentsInBlock = UpdateCommentsDtoHelper.getCommentsInBlock(allComments, [blockId]);
    const comments = commentsInBlock.map(({ commentId, editorSelection }) => {
      if (editorSelection?.sectionId !== targetSectionId) {
        return { commentId, editorSelection };
      }

      return {
        commentId,
        editorSelection: {
          ...editorSelection,
          sectionId: undefined,
          rowId: undefined,
          cellId: undefined,
          nodeId: undefined,
          textSelection: undefined,
          detachedFromContent: true,
        },
      };
    });

    return { comments };
  }

  static getSplitRowDto(
    allComments: Comment[],
    blockId: string,
    payload: SplitTextCreateRow
  ): UpdateCommentsDto {
    const commentsInBlock = UpdateCommentsDtoHelper.getCommentsInBlock(allComments, [blockId]);
    const comments = commentsInBlock.map(({ commentId, editorSelection }) => {
      if (editorSelection?.nodeId !== payload.targetNodeId || !editorSelection?.textSelection) {
        return { commentId, editorSelection };
      }

      const { textSelection } = editorSelection;
      const from = Math.min(textSelection.anchor, textSelection.head);
      const to = Math.max(textSelection.anchor, textSelection.head);

      // comments in the first node
      if (from < payload.firstNodeLength - 1) {
        const head = Math.min(to, payload.firstNodeLength - 1);
        return {
          commentId,
          editorSelection: {
            ...editorSelection,
            textSelection: {
              ...textSelection,
              anchor: from,
              head,
            },
          },
        };
      }

      const lastNodeOffset = payload.cutPosition + payload.trimmedLength - 1;

      // comments in the last node
      if (from >= lastNodeOffset) {
        return {
          commentId,
          editorSelection: {
            ...editorSelection,
            nodeId: SPLIT_ROW_SECOND_NODE_ID_PLACEHOLDER,
            textSelection: {
              ...textSelection,
              anchor: from - lastNodeOffset,
              head: to - lastNodeOffset,
            },
          },
        };
      }

      // comments in the deleted middle space
      return {
        commentId,
        editorSelection: {
          ...editorSelection,
          detachedFromContent: true,
          textSelection: undefined,
        },
      };
    });

    return { comments };
  }
}
